/**
 * RecognitionDetailModal — Detalhe do reconhecimento por trás de uma figurinha (RN-30).
 *
 * Renderiza num host próprio anexado ao <body> (NÃO em #modal: o react-modal do
 * CopaModal marca #modal com aria-hidden, escondendo o diálogo do leitor de tela).
 * Enquanto aberta, marca todos os outros filhos do <body> com aria-hidden — assim
 * o cursor virtual do JAWS fica confinado ao diálogo (aria-modal sozinho não basta
 * para o JAWS). Gerencia foco com capture:true em ambos os listeners para ter
 * prioridade sobre o react-modal pai.
 */

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { getRecognition, RecognitionDetail } from 'services/copa.service';
import { ColleagueFace } from './components/ColleagueFace';
import Humanograma from 'components/Humanograma';
import styles from './RecognitionDetailModal.module.scss';

const BEHAVIOR_LABEL: Record<string, string> = {
  foco: 'Foco no cliente',
  colaboracao: 'Colaboração',
  inovacao: 'Inovação',
  resolutividade: 'Protagonismo',
  lideranca: 'Liderança Integradora',
};

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface RecognitionDetailModalProps {
  recognitionId: string | null;
  onClose: () => void;
}

export function RecognitionDetailModal({ recognitionId, onClose }: RecognitionDetailModalProps) {
  const [data, setData] = useState<RecognitionDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const announcedRef = useRef(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const isOpen = !!recognitionId;

  // Host de portal dedicado, anexado direto ao <body>. NÃO usamos #modal: o
  // react-modal do CopaModal marca #modal com aria-hidden="true" (appElement),
  // então um diálogo plantado lá nasce escondido para o leitor de tela — o JAWS
  // pula o conteúdo e segue lendo o álbum. Aqui ele vira um irmão do <body>.
  const [portalHost] = useState<HTMLDivElement | null>(() =>
    typeof document === 'undefined' ? null : document.createElement('div'),
  );

  useEffect(() => {
    if (!portalHost) return;
    document.body.appendChild(portalHost);
    return () => {
      document.body.removeChild(portalHost);
    };
  }, [portalHost]);

  // Enquanto aberta, esconde TODO o resto do <body> do leitor de tela — inclusive
  // o álbum, que o react-modal renderiza num portal próprio no <body> e NÃO
  // esconde. O focus trap abaixo já cuida do Tab; isto confina o cursor virtual
  // do JAWS, pois aria-modal="true" sozinho não é respeitado por ele. Preserva o
  // valor anterior de cada irmão e restaura ao fechar (ex.: #modal continua
  // aria-hidden por conta do react-modal).
  useEffect(() => {
    if (!isOpen || !portalHost) return;

    const siblings = Array.from(document.body.children).filter(
      el => el !== portalHost,
    ) as HTMLElement[];
    const previous = siblings.map(el => el.getAttribute('aria-hidden'));
    siblings.forEach(el => el.setAttribute('aria-hidden', 'true'));

    return () => {
      siblings.forEach((el, i) => {
        const prev = previous[i];
        if (prev === null) el.removeAttribute('aria-hidden');
        else el.setAttribute('aria-hidden', prev);
      });
    };
  }, [isOpen, portalHost]);

  // Fetch
  useEffect(() => {
    let alive = true;
    if (!recognitionId) { setData(null); setError(false); return; }
    // Zera o conteúdo anterior a cada abertura: garante um estado de loading
    // limpo (sem dado obsoleto) e faz o anúncio abaixo esperar o dado FRESCO —
    // é a transição foco Fechar → container, no load, que dispara a fala.
    setData(null);
    setLoading(true);
    setError(false);
    getRecognition(recognitionId)
      .then(d => { if (!alive) return; if (d) setData(d); else setError(true); })
      .catch(() => alive && setError(true))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [recognitionId]);

  // Focus trap — capture:true garante prioridade sobre o react-modal do CopaModal pai
  useEffect(() => {
    if (!isOpen) return;

    previousFocusRef.current = document.activeElement as HTMLElement | null;

    const rafId = requestAnimationFrame(() => {
      const container = dialogRef.current;
      if (!container) return;
      const first = container.querySelectorAll<HTMLElement>(FOCUSABLE)[0] ?? container;
      first.focus();
    });

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab') return;

      const container = dialogRef.current;
      if (!container) return;

      const focusable = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusable.length === 0) { e.preventDefault(); e.stopPropagation(); return; }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (!container.contains(document.activeElement) || document.activeElement === first) {
          e.preventDefault();
          e.stopPropagation();
          last.focus();
        }
      } else {
        if (!container.contains(document.activeElement) || document.activeElement === last) {
          e.preventDefault();
          e.stopPropagation();
          first.focus();
        }
      }
    }

    function handleFocusIn(e: FocusEvent) {
      const container = dialogRef.current;
      if (!container) return;
      if (!container.contains(e.target as Node)) {
        const first = container.querySelectorAll<HTMLElement>(FOCUSABLE)[0] ?? container;
        first.focus();
      }
    }

    // capture:true = dispara antes de qualquer handler de bubbling (inclusive react-modal)
    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('focusin', handleFocusIn, true);

    return () => {
      cancelAnimationFrame(rafId);
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('focusin', handleFocusIn, true);
      previousFocusRef.current?.focus();
      previousFocusRef.current = null;
    };
  }, [isOpen]);

  // A11Y: quando o conteúdo termina de carregar, move o foco para o CONTAINER do
  // diálogo (role="dialog" + aria-labelledby + aria-describedby). A descrição
  // (aria-describedby) só é lida quando o próprio elemento que a possui recebe o
  // foco — o efeito acima foca o botão Fechar (que não carrega a descrição) e, na
  // abertura, o conteúdo ainda está carregando (os alvos das ids nem existem).
  // Focar o container após o load faz o leitor anunciar nome + comportamento +
  // data + mensagem. Uma vez por abertura (announcedRef).
  useEffect(() => {
    if (!isOpen) {
      announcedRef.current = false;
      return;
    }
    if (announcedRef.current) return;
    // Espera o conteúdo (ou o erro) realmente renderizar — os alvos das ids de
    // aria-labelledby/aria-describedby só existem depois do fetch. `loading`
    // começa false (antes do fetch), então guardar por !loading focaria cedo
    // demais, com o container ainda vazio.
    if (!data && !error) return;
    const container = dialogRef.current;
    if (!container) return;
    announcedRef.current = true;
    const id = requestAnimationFrame(() => container.focus());
    return () => cancelAnimationFrame(id);
  }, [isOpen, data, error]);

  if (!isOpen) return null;

  const portalTarget = portalHost ?? document.body;

  const name = data?.colleagueName ?? data?.colleagueId ?? '';
  const initial = (name || '?').charAt(0).toUpperCase();
  const directionPrefix = data?.direction === 'recebido' ? 'Você foi reconhecido por' : 'Você reconheceu';
  const dateText = data?.time
    ? format(new Date(data.time), "d 'de' MMMM 'de' yyyy", { locale: ptBR })
    : '';

  return createPortal(
    <div className={styles.overlay} onClick={onClose}>
      <div
        ref={dialogRef}
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        // A11Y: o nome acessível reúne título + comportamento + data + mensagem.
        // A aria-describedby não era falada por todos os leitores (ex.: NVDA no
        // container), mas o NOME é sempre anunciado ao focar — então todo o
        // conteúdo é lido ao abrir, sem depender de navegação por Tab (a mensagem
        // é texto, não é um stop de Tab).
        aria-labelledby="recognition-detail-title recognition-detail-behavior recognition-detail-date recognition-detail-message"
        tabIndex={-1}
        onClick={e => e.stopPropagation()}
      >
        <button className={styles.closeBtn} onClick={onClose} type="button" aria-label="Fechar">
          ✕
        </button>

        {loading && (
          <div className={styles.state} role="status" aria-live="polite">
            <div className={styles.spinner} aria-hidden="true" />
            <p>Carregando reconhecimento...</p>
          </div>
        )}

        {!loading && error && (
          <div className={styles.state} role="alert">
            <p>Não foi possível carregar este reconhecimento.</p>
            <button className={styles.ctaPrimary} onClick={onClose} type="button">Fechar</button>
          </div>
        )}

        {!loading && data && (
          <div className={styles.content}>
            <div className={styles.head}>
              <div className={styles.face} aria-hidden="true">
                <ColleagueFace
                  image={data.image}
                  avatar={data.avatar}
                  avatarType={data.avatarType}
                  initial={initial}
                  size={72}
                  initialClassName={styles.faceInitial}
                />
              </div>
              <div className={styles.headText}>
                <div
                  className={styles.direction}
                  id="recognition-detail-title"
                  role="heading"
                  aria-level={2}
                >
                  {directionPrefix}{' '}
                  <Humanograma
                    data={{
                      _id: data.colleagueId,
                      nome: name,
                      funcao: data.funcao,
                      prefixo: data.prefixo,
                      nome_dependencia: data.nome_dependencia,
                    }}
                  >
                    <span className={styles.nameLink}>{name}</span>
                  </Humanograma>
                </div>
                <div className={styles.behavior} id="recognition-detail-behavior">
                  {BEHAVIOR_LABEL[data.behavior] ?? data.behavior}
                </div>
                {dateText && <div className={styles.date} id="recognition-detail-date">{dateText}</div>}
              </div>
            </div>

            {data.message ? (
              <div className={styles.messageBox} id="recognition-detail-message">
                <div className={styles.messageLabel}>Mensagem</div>
                <div
                  className={styles.messageBody}
                  dangerouslySetInnerHTML={{ __html: data.message }}
                />
              </div>
            ) : (
              <div className={styles.messageBox} id="recognition-detail-message">
                <div className={styles.messageEmpty}>Sem mensagem neste reconhecimento.</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>,
    portalTarget,
  );
}
